import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import apiOffers from 'api/offers';
import IPriceListTemplate from 'api/interfaces/offers/IPriceListTemplate';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

import 'style/layout/modal/ModalConfirm.css';

/**
 * Fetches an additional price list of the registry to the offer: its rows with their
 * calculations are added after the rows the offer already has.
 */
const FetchPriceListTemplate = ({
  additionalOffer,
  isOpen,
  offerId,
  onFetched,
  rowCount,
  toggle,
}: {
  additionalOffer: boolean;
  isOpen: boolean;
  offerId: number;
  onFetched: () => void;
  // The rows the offer already has: the fetched ones come after them
  rowCount: number;
  toggle: () => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.offers}:priceListRegistry.${key}`, options);

  const [lists, setLists] = useState<IPriceListTemplate[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setTemplateId('');
    setLists(null);
    apiOffers.priceListTemplates
      .get()
      .then(setLists)
      .catch(() => setLists([]));
  }, [isOpen]);

  const chosen = lists?.find((list) => String(list.id) === templateId);

  const fetch = async () => {
    if (chosen === undefined || fetching) {
      return;
    }

    setFetching(true);
    try {
      const { count } = await apiOffers.priceListTemplates.fetch(chosen.id, {
        additionalOfferId: additionalOffer ? offerId : undefined,
        offerId: additionalOffer ? undefined : offerId,
      });
      dispatch(addMessage(tr('Fetched', { count })));
      onFetched();
      toggle();
    } catch (error) {
      dispatch(addMessage(tr('Fetch failed')));
    } finally {
      setFetching(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader className="modal-header" toggle={toggle}>
        {tr('Fetch title')}
      </ModalHeader>
      <ModalBody>
        <p>{tr('Fetch info')}</p>
        {lists !== null && lists.length === 0 ? (
          <p>{tr('No lists')}</p>
        ) : (
          <FormGroup>
            <Label for="price-list-registry-fetch">{tr('Choose')}</Label>
            <Input
              disabled={lists === null}
              id="price-list-registry-fetch"
              onChange={(event) => setTemplateId(event.target.value)}
              type="select"
              value={templateId}
            >
              <option value="">{lists === null ? '…' : tr('Choose')}</option>
              {(lists ?? []).map((list) => (
                <option key={list.id} value={list.id}>
                  {`${list.name} · ${list.organizationUnitName ?? ''} · ${tr(
                    'Rows',
                    { count: list.rowCount }
                  )}`}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}
        {/* The rows the offer has stay: the fetched ones come after them */}
        {chosen !== undefined && rowCount > 0 ? (
          <p className="text-muted">
            {tr('Fetch after rows', { count: rowCount })}
          </p>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button
          className="modal-button"
          disabled={chosen === undefined || fetching}
          onClick={fetch}
        >
          {tr('Fetch')}
        </Button>
        <Button className="modal-button" onClick={toggle}>
          {tr('Cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FetchPriceListTemplate;
